import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Printer, 
  Download, 
  ChevronDown,
  User,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Eye,
  MoreHorizontal
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const EstadoCuenta = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [businessConfig, setBusinessConfig] = useState(null);
  
  // Estados para el modal de detalle de factura
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchBusinessConfig();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientInvoices(selectedClientId);
    } else {
      setInvoices([]);
      setSelectedClient(null);
    }
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      // Filtrar solo clientes que tienen facturas a crédito pendientes
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const fetchClientInvoices = async (clientId) => {
    setLoadingInvoices(true);
    try {
      // Obtener datos del cliente
      const client = clients.find(c => c.id === clientId);
      setSelectedClient(client);
      
      // Obtener cuentas por cobrar del cliente
      const response = await axios.get(`${API}/accounts-receivable`);
      
      // Filtrar por cliente_id O por nombre del cliente (para facturas antiguas sin cliente_id)
      const clientAccounts = response.data.filter(account => {
        if (account.status !== 'pendiente') return false;
        
        // Buscar por cliente_id si existe
        if (account.cliente_id && account.cliente_id === clientId) return true;
        
        // Buscar por nombre del cliente si no tiene cliente_id
        if (!account.cliente_id && client && account.cliente_nombre === client.nombre) return true;
        
        return false;
      });
      
      setInvoices(clientAccounts);
    } catch (error) {
      toast.error('Error al cargar facturas del cliente');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDaysUntilDue = (dateStr) => {
    if (!dateStr) return 0;
    const dueDate = new Date(dateStr);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTotalPendiente = () => {
    return invoices.reduce((sum, inv) => sum + (inv.monto_pendiente || 0), 0);
  };

  const getTotalFacturas = () => {
    return invoices.reduce((sum, inv) => sum + (inv.monto_total || 0), 0);
  };

  const getTotalPagado = () => {
    return invoices.reduce((sum, inv) => sum + (inv.monto_pagado || 0), 0);
  };

  // Obtener detalle de una factura específica
  const viewInvoiceDetail = async (accountReceivable) => {
    setLoadingInvoiceDetail(true);
    setInvoiceDetailOpen(true);
    
    try {
      // Obtener la factura completa usando el factura_id
      const response = await axios.get(`${API}/invoices/${accountReceivable.factura_id}`);
      setSelectedInvoiceDetail(response.data);
    } catch (error) {
      console.error('Error al cargar factura:', error);
      toast.error('Error al cargar los detalles de la factura');
      setInvoiceDetailOpen(false);
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  // Imprimir factura individual en formato POS (80mm) - MISMO FORMATO QUE FACTURACIÓN
  const printInvoicePOS = async (accountReceivable) => {
    try {
      const response = await axios.get(`${API}/invoices/${accountReceivable.factura_id}`);
      const invoice = response.data;
      
      // Función auxiliar para formatear fecha/hora en impresión
      const formatPrintDateTime = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      };
      
      const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
      const printWindow = window.open('', '', 'width=302,height=600');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Factura ${invoice.numero_factura}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              @page { size: 80mm auto; margin: 0; }
              @media print { body { margin: 0 !important; padding: 0 !important; } }
              body { 
                font-family: 'Courier New', Courier, monospace; 
                font-size: 11px; line-height: 1.4; width: 80mm; max-width: 80mm;
                margin: 0; padding: 3mm; color: #000; background: #fff;
              }
              .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
              .logo { max-width: 60mm; max-height: 25mm; margin: 0 auto 3mm; display: block; }
              h2 { margin: 2mm 0; font-size: 14px; font-weight: bold; }
              p { margin: 1mm 0; font-size: 10px; }
              .info { margin-bottom: 3mm; font-size: 10px; }
              .info-row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 10px; }
              .separator { border-top: 1px dashed #000; margin: 3mm 0; }
              .items { margin: 3mm 0; }
              .item-row { margin: 2mm 0; }
              .item-detail { display: flex; justify-content: space-between; font-size: 10px; }
              .totals { margin-top: 3mm; border-top: 1px dashed #000; padding-top: 3mm; }
              .total-row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 11px; }
              .grand-total { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 3mm; margin-top: 2mm; }
              .footer { text-align: center; margin-top: 5mm; font-size: 9px; border-top: 1px dashed #000; padding-top: 3mm; }
              .bold { font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="header">
              ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
              <h2>${config.nombre_comercio || 'LICORSTORE RD'}</h2>
              ${config.rnc ? `<p>RNC: ${config.rnc}</p>` : ''}
              ${config.telefono ? `<p>Tel: ${config.telefono}</p>` : ''}
              ${config.direccion ? `<p style="font-size: 9px;">${config.direccion}</p>` : ''}
              <p class="bold">FACTURA</p>
            </div>
            
            <div class="info">
              <div class="info-row"><span>No:</span><span class="bold">${invoice.numero_factura}</span></div>
              <div class="info-row"><span>NCF:</span><span class="bold">${invoice.ncf || 'N/A'}</span></div>
              <div class="info-row"><span>Fecha:</span><span>${formatPrintDateTime(invoice.fecha)}</span></div>
              <div class="info-row"><span>Cajero:</span><span>${invoice.cajero_nombre || ''}</span></div>
              <div class="info-row"><span>Pago:</span><span>${invoice.tipo_pago || 'Crédito'}</span></div>
            </div>

            <div class="separator"></div>

            <div class="info">
              <div class="bold" style="margin-bottom: 2mm;">CLIENTE:</div>
              <div>${invoice.cliente.nombre}</div>
              ${invoice.cliente.rnc && invoice.cliente.rnc !== '000000000' ? `<div style="font-size: 9px;">RNC: ${invoice.cliente.rnc}</div>` : ''}
            </div>

            <div class="separator"></div>

            <div class="items">
              <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 10px; margin-bottom: 2mm; border-bottom: 1px solid #000; padding-bottom: 1mm;">
                <span style="flex: 2;">Producto</span>
                <span style="flex: 1; text-align: center;">Cant</span>
                <span style="flex: 1.5; text-align: right;">Total</span>
              </div>
              ${invoice.items.map(item => `
                <div class="item-row">
                  <div style="font-size: 10px; font-weight: bold; margin-bottom: 1mm;">${item.producto_nombre}</div>
                  <div class="item-detail">
                    <span style="flex: 2;">${item.cantidad} x $${(item.precio_unitario || 0).toFixed(2)}</span>
                    <span style="flex: 1.5; text-align: right; font-weight: bold;">$${(item.subtotal || 0).toFixed(2)}</span>
                  </div>
                </div>
              `).join('')}
            </div>

            <div class="totals">
              <div class="total-row">
                <span>Subtotal:</span>
                <span class="bold">RD$ ${(invoice.subtotal || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              ${invoice.itbis > 0 ? `
                <div class="total-row">
                  <span>ITBIS (18%):</span>
                  <span class="bold">RD$ ${(invoice.itbis || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              ` : ''}
              <div class="total-row grand-total">
                <span>TOTAL:</span>
                <span>RD$ ${(invoice.total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>

            <div class="footer">
              <p>¡Gracias por su compra!</p>
              <p>${config.nombre_comercio || 'LICORSTORE RD'}</p>
              <p style="margin-top: 3mm;">***</p>
            </div>

            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 100);
                }, 250);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error al imprimir:', error);
      toast.error('Error al cargar la factura para imprimir');
    }
  };

  // Imprimir factura individual en formato A4 - MISMO FORMATO QUE FACTURACIÓN
  const printInvoiceA4 = async (accountReceivable) => {
    try {
      const response = await axios.get(`${API}/invoices/${accountReceivable.factura_id}`);
      const invoice = response.data;
      
      const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
      const printWindow = window.open('', '', 'width=900,height=700');
      
      const clientCode = invoice.cliente.codigo_cliente || selectedClient?.codigo_cliente || '-';
      const subtotalSinItbis = invoice.subtotal || (invoice.total - (invoice.itbis || 0));
      const esCredito = invoice.cliente.rnc && invoice.cliente.rnc !== '000000000';
      const tipoComprobante = esCredito ? 'CRÉDITO FISCAL' : 'CONSUMIDOR FINAL';
      
      const formatMoney = (value) => {
        return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
      };
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Factura ${invoice.numero_factura}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              @page { size: A4; margin: 15mm; }
              @media print { body { margin: 0 !important; } }
              body { 
                font-family: 'Helvetica Neue', Arial, sans-serif; 
                font-size: 10px; line-height: 1.4; color: #333;
                background: #fff; padding: 20mm;
              }
              .header {
                display: flex; justify-content: space-between; align-items: flex-start;
                padding-bottom: 20px; border-bottom: 2px solid #000; margin-bottom: 20px;
              }
              .company { max-width: 60%; }
              .company-name { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 8px; }
              .company-info { font-size: 9px; color: #666; line-height: 1.6; }
              .invoice-header { text-align: right; }
              .invoice-title { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #666; margin-bottom: 5px; }
              .invoice-number { font-size: 20px; font-weight: 700; margin-bottom: 10px; }
              .invoice-type {
                font-size: 9px; text-transform: uppercase; letter-spacing: 1px;
                padding: 8px 15px; border: 1px solid #333; display: inline-block;
                text-align: center; line-height: 1.8;
              }
              .invoice-type span { display: block; margin-top: 4px; }
              .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 25px; }
              .detail-section h3 {
                font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px;
                color: #999; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #eee;
              }
              .detail-row { display: flex; margin-bottom: 6px; font-size: 10px; }
              .detail-label { width: 80px; color: #666; }
              .detail-value { flex: 1; font-weight: 500; }
              .table-section { margin-bottom: 25px; }
              .table-title { font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 10px; }
              table { width: 100%; border-collapse: collapse; }
              thead th {
                text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 1px;
                color: #666; padding: 10px 8px; border-bottom: 2px solid #333; font-weight: 600;
              }
              thead th.right { text-align: right; }
              thead th.center { text-align: center; }
              tbody td { padding: 10px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
              tbody td.right { text-align: right; font-family: 'Courier New', monospace; }
              tbody td.center { text-align: center; }
              tbody tr:last-child td { border-bottom: 2px solid #333; }
              .item-count { font-size: 9px; color: #666; margin-top: 10px; text-align: right; }
              .totals-section { display: flex; justify-content: flex-end; margin-bottom: 30px; }
              .totals-box { width: 250px; }
              .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 10px; }
              .totals-row.subtotal { border-bottom: 1px solid #eee; }
              .totals-row.total { border-top: 2px solid #333; margin-top: 5px; padding-top: 12px; }
              .totals-row.total .label, .totals-row.total .value { font-size: 14px; font-weight: 700; }
              .totals-row .label { color: #666; }
              .totals-row .value { font-family: 'Courier New', monospace; font-weight: 500; }
              .footer { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; padding-top: 20px; border-top: 1px solid #eee; }
              .footer-section h4 { font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 15px; }
              .signature-area { height: 60px; border-bottom: 1px solid #333; }
              .signature-label { font-size: 8px; color: #999; text-align: center; margin-top: 5px; }
              .footer-info { font-size: 9px; color: #666; line-height: 1.6; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company">
                <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
                <div class="company-info">
                  ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                  ${config.direccion ? `${config.direccion}<br>` : ''}
                  ${config.telefono ? `Tel: ${config.telefono}` : ''}
                </div>
              </div>
              <div class="invoice-header">
                <div class="invoice-title">Factura</div>
                <div class="invoice-number">${invoice.numero_factura}</div>
                <div class="invoice-type">${tipoComprobante}${invoice.ncf && invoice.ncf !== 'N/A' ? `<span style="font-family: 'Courier New', monospace; font-size: 10px; font-weight: 600;">NCF: ${invoice.ncf}</span>` : ''}</div>
              </div>
            </div>
            
            <div class="details-grid">
              <div class="detail-section">
                <h3>Cliente</h3>
                <div class="detail-row">
                  <span class="detail-label">Código</span>
                  <span class="detail-value">${clientCode}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Nombre</span>
                  <span class="detail-value">${invoice.cliente.nombre}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">RNC/Cédula</span>
                  <span class="detail-value">${invoice.cliente.rnc || invoice.cliente.cedula || '—'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Teléfono</span>
                  <span class="detail-value">${invoice.cliente.telefono || '—'}</span>
                </div>
              </div>
              <div class="detail-section">
                <h3>Factura</h3>
                <div class="detail-row">
                  <span class="detail-label">Fecha</span>
                  <span class="detail-value">${invoice.fecha}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Condición</span>
                  <span class="detail-value">${invoice.es_credito ? 'Crédito' : 'Contado'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Pago</span>
                  <span class="detail-value">${invoice.es_credito ? 'Crédito' : (invoice.tipo_pago || 'Efectivo')}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Vendedor</span>
                  <span class="detail-value">${invoice.cajero_nombre || 'Admin'}</span>
                </div>
              </div>
            </div>
            
            <div class="table-section">
              <div class="table-title">Detalle</div>
              <table>
                <thead>
                  <tr>
                    <th style="width:30px" class="center">#</th>
                    <th>Descripción</th>
                    <th class="right" style="width:80px">Precio</th>
                    <th class="center" style="width:50px">Cant.</th>
                    <th class="right" style="width:80px">Subtotal</th>
                    <th class="right" style="width:70px">ITBIS</th>
                    <th class="right" style="width:90px">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoice.items.map((item, index) => {
                    const itemSubtotal = item.precio_unitario * item.cantidad;
                    const itemItbis = item.itbis || (itemSubtotal * 0.18);
                    const itemTotal = itemSubtotal + itemItbis;
                    return `
                      <tr>
                        <td class="center">${index + 1}</td>
                        <td>${item.producto_nombre}</td>
                        <td class="right">${formatMoney(item.precio_unitario)}</td>
                        <td class="center">${item.cantidad}</td>
                        <td class="right">${formatMoney(itemSubtotal)}</td>
                        <td class="right">${formatMoney(itemItbis || 0)}</td>
                        <td class="right">${formatMoney(itemTotal)}</td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
              <div class="item-count">${invoice.items.length} artículo(s)</div>
            </div>
            
            <div class="totals-section">
              <div class="totals-box">
                <div class="totals-row subtotal">
                  <span class="label">Subtotal</span>
                  <span class="value">RD$ ${formatMoney(subtotalSinItbis)}</span>
                </div>
                <div class="totals-row">
                  <span class="label">Descuento</span>
                  <span class="value">RD$ ${formatMoney(invoice.descuento || 0)}</span>
                </div>
                <div class="totals-row">
                  <span class="label">ITBIS (18%)</span>
                  <span class="value">RD$ ${formatMoney(invoice.itbis || 0)}</span>
                </div>
                <div class="totals-row total">
                  <span class="label">Total</span>
                  <span class="value">RD$ ${formatMoney(invoice.total)}</span>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <div class="footer-section">
                <h4>Elaborado por</h4>
                <div class="footer-info">
                  ${invoice.cajero_nombre || 'Administrador'}<br>
                  ${config.nombre_comercio || ''}
                </div>
              </div>
              <div class="footer-section">
                <h4>Recibido por</h4>
                <div class="signature-area"></div>
                <div class="signature-label">Firma</div>
              </div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 100);
                }, 250);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error al imprimir:', error);
      toast.error('Error al cargar la factura para imprimir');
    }
  };

  // Imprimir Estado de Cuenta en formato POS (80mm) - Formato Formal
  const printPOS = () => {
    if (!selectedClient || invoices.length === 0) {
      toast.error('Seleccione un cliente con facturas pendientes');
      return;
    }

    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const printWindow = window.open('', '', 'width=302,height=600');
    
    // Formatear fecha con hora en formato 12H
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    let hours = currentDate.getHours();
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedDate = `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    
    // Función local para formatear fechas dentro del template
    const formatDateLocal = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    // Función local para calcular días hasta vencimiento
    const getDaysUntilDueLocal = (dateStr) => {
      if (!dateStr) return 0;
      const dueDate = new Date(dateStr);
      const today = new Date();
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };
    
    // Pre-calcular los datos de las facturas
    const invoicesData = invoices.map(inv => {
      const daysUntilDue = getDaysUntilDueLocal(inv.fecha_vencimiento);
      const isOverdue = daysUntilDue < 0;
      return {
        numero: inv.factura_numero,
        fecha: formatDateLocal(inv.fecha_factura),
        vencimiento: formatDateLocal(inv.fecha_vencimiento),
        total: (inv.monto_total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2}),
        pagado: (inv.monto_pagado || 0).toLocaleString('es-DO', {minimumFractionDigits: 2}),
        pendiente: (inv.monto_pendiente || 0).toLocaleString('es-DO', {minimumFractionDigits: 2}),
        estado: isOverdue ? 'VENCIDA' : 'PENDIENTE'
      };
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Estado de Cuenta - ${selectedClient.nombre}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; }
            @page { size: 80mm auto; margin: 0; }
            @media print { 
              body { margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; } 
            }
            body { 
              font-family: 'Lucida Console', 'Courier New', monospace; 
              font-size: 12px; 
              line-height: 1.3; 
              width: 80mm; 
              max-width: 80mm;
              margin: 0; 
              padding: 4mm; 
              color: #000; 
              background: #fff;
              font-weight: 500;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #000; 
              padding-bottom: 4mm; 
              margin-bottom: 4mm; 
            }
            .logo { max-width: 55mm; max-height: 20mm; margin: 0 auto 3mm; display: block; }
            h2 { margin: 2mm 0; font-size: 16px; font-weight: 900; letter-spacing: 0.5px; }
            p { margin: 1mm 0; font-size: 11px; font-weight: 600; }
            .doc-title { 
              text-align: center; 
              font-size: 14px; 
              font-weight: 900; 
              border: 2px solid #000; 
              padding: 3mm; 
              margin: 4mm 0;
              letter-spacing: 1px;
              background: #f5f5f5;
            }
            .info { margin-bottom: 3mm; font-size: 11px; }
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 2mm 0; 
              font-size: 11px; 
              font-weight: 600;
            }
            .separator { border-top: 1px dashed #000; margin: 4mm 0; }
            .section-title { 
              font-weight: 900; 
              font-size: 12px; 
              margin: 3mm 0; 
              border-bottom: 2px solid #000; 
              padding-bottom: 2mm; 
              letter-spacing: 0.5px;
            }
            .items { margin: 3mm 0; }
            .item-row { 
              margin: 3mm 0; 
              padding: 3mm; 
              border: 1px solid #000; 
              font-size: 10px; 
              background: #fafafa;
            }
            .item-header { 
              display: flex; 
              justify-content: space-between; 
              font-weight: 900; 
              font-size: 11px;
              margin-bottom: 2mm;
              border-bottom: 1px dotted #666;
              padding-bottom: 2mm;
            }
            .item-detail { 
              display: flex; 
              justify-content: space-between; 
              margin: 1mm 0;
              font-weight: 600;
            }
            .totals { margin-top: 4mm; border-top: 2px solid #000; padding-top: 4mm; }
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 2mm 0; 
              font-size: 12px; 
              font-weight: 700;
            }
            .grand-total { 
              font-weight: 900; 
              font-size: 14px; 
              border: 2px solid #000; 
              padding: 3mm; 
              margin-top: 3mm; 
              background: #e0e0e0; 
            }
            .footer { 
              text-align: center; 
              margin-top: 5mm; 
              font-size: 10px; 
              border-top: 1px dashed #000; 
              padding-top: 4mm; 
              font-weight: 600;
            }
            .bold { font-weight: 900; }
          </style>
        </head>
        <body>
          <div class="header">
            ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
            <h2>${config.nombre_comercio || 'LICORSTORE RD'}</h2>
            ${config.rnc ? `<p>RNC: ${config.rnc}</p>` : ''}
            ${config.telefono ? `<p>Tel: ${config.telefono}</p>` : ''}
            ${config.direccion ? `<p style="font-size: 10px;">${config.direccion}</p>` : ''}
          </div>
          
          <div class="doc-title">ESTADO DE CUENTA</div>
          
          <div class="info">
            <div class="info-row"><span>Fecha Emisión:</span><span class="bold">${formattedDate}</span></div>
            <div class="info-row"><span>No. Facturas:</span><span class="bold">${invoices.length}</span></div>
          </div>

          <div class="separator"></div>

          <div class="section-title">DATOS DEL CLIENTE</div>
          <div class="info">
            ${selectedClient.codigo_cliente ? `<div class="info-row"><span>Código:</span><span class="bold">${selectedClient.codigo_cliente}</span></div>` : ''}
            <div style="font-weight: 900; font-size: 12px; margin: 2mm 0;">${selectedClient.nombre}</div>
            ${selectedClient.rnc ? `<div style="font-size: 10px; font-weight: 600;">RNC: ${selectedClient.rnc}</div>` : ''}
            ${selectedClient.telefono ? `<div style="font-size: 10px; font-weight: 600;">Tel: ${selectedClient.telefono}</div>` : ''}
            ${selectedClient.direccion ? `<div style="font-size: 10px; font-weight: 600;">${selectedClient.direccion}</div>` : ''}
          </div>

          <div class="separator"></div>

          <div class="section-title">DETALLE DE FACTURAS</div>
          <div class="items">
            ${invoicesData.map(inv => `
              <div class="item-row">
                <div class="item-header">
                  <span>${inv.numero}</span>
                  <span>${inv.estado}</span>
                </div>
                <div class="item-detail">
                  <span>Fecha:</span>
                  <span>${inv.fecha}</span>
                </div>
                <div class="item-detail">
                  <span>Vence:</span>
                  <span>${inv.vencimiento}</span>
                </div>
                <div class="item-detail">
                  <span>Total:</span>
                  <span>RD$ ${inv.total}</span>
                </div>
                <div class="item-detail">
                  <span>Pagado:</span>
                  <span>RD$ ${inv.pagado}</span>
                </div>
                <div class="item-detail" style="font-weight: 900; font-size: 11px; margin-top: 2mm;">
                  <span>PENDIENTE:</span>
                  <span>RD$ ${inv.pendiente}</span>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="totals">
            <div class="total-row">
              <span>Total Facturado:</span>
              <span>RD$ ${getTotalFacturas().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row">
              <span>Total Abonado:</span>
              <span>RD$ ${getTotalPagado().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
            </div>
            <div class="total-row grand-total">
              <span>SALDO PENDIENTE:</span>
              <span>RD$ ${getTotalPendiente().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
            </div>
          </div>

          <div class="footer">
            <p style="font-weight: 900;">Documento informativo</p>
            <p style="margin-top: 2mm;">${config.nombre_comercio || 'LICORSTORE RD'}</p>
            <p style="margin-top: 2mm;">Fecha: ${formattedDate}</p>
            <p style="margin-top: 3mm;">* * *</p>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Imprimir Estado de Cuenta en formato A4 - Formato Formal Profesional
  const printA4 = () => {
    if (!selectedClient || invoices.length === 0) {
      toast.error('Seleccione un cliente con facturas pendientes');
      return;
    }

    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const printWindow = window.open('', '', 'width=900,height=700');
    
    // Formatear fecha con hora en formato 12H
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    let hours = currentDate.getHours();
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedDate = `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    
    // Función para formatear moneda
    const formatMoney = (value) => {
      return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };
    
    // Función local para formatear fechas
    const formatDateLocal = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
    };
    
    // Función local para calcular días hasta vencimiento
    const getDaysUntilDueLocal = (dateStr) => {
      if (!dateStr) return 0;
      const dueDate = new Date(dateStr);
      const today = new Date();
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };
    
    // Pre-calcular los datos de las facturas
    const invoicesData = invoices.map(inv => {
      const daysUntilDue = getDaysUntilDueLocal(inv.fecha_vencimiento);
      const isOverdue = daysUntilDue < 0;
      return {
        numero: inv.factura_numero,
        fecha: formatDateLocal(inv.fecha_factura),
        vencimiento: formatDateLocal(inv.fecha_vencimiento),
        total: formatMoney(inv.monto_total),
        pagado: formatMoney(inv.monto_pagado),
        pendiente: formatMoney(inv.monto_pendiente),
        isOverdue: isOverdue,
        diasTexto: isOverdue ? `Vencida (${Math.abs(daysUntilDue)}d)` : `${daysUntilDue} días`
      };
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Estado de Cuenta - ${selectedClient.nombre}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 15mm; }
            @media print { body { margin: 0 !important; } }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              font-size: 10px; line-height: 1.4; color: #333;
              background: #fff; padding: 20mm;
            }
            
            /* Header */
            .header {
              display: flex; justify-content: space-between; align-items: flex-start;
              padding-bottom: 20px; border-bottom: 2px solid #000; margin-bottom: 20px;
            }
            .company { max-width: 60%; }
            .company-name { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 8px; }
            .company-info { font-size: 9px; color: #666; line-height: 1.6; }
            .doc-header { text-align: right; }
            .doc-title { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #666; margin-bottom: 5px; }
            .doc-type { font-size: 20px; font-weight: 700; margin-bottom: 10px; }
            .doc-date {
              font-size: 9px; text-transform: uppercase; letter-spacing: 1px;
              padding: 8px 15px; border: 1px solid #333; display: inline-block;
            }
            
            /* Details Grid */
            .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 25px; }
            .detail-section h3 {
              font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px;
              color: #999; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #eee;
            }
            .detail-row { display: flex; margin-bottom: 6px; font-size: 10px; }
            .detail-label { width: 90px; color: #666; }
            .detail-value { flex: 1; font-weight: 500; }
            
            /* Summary Box */
            .summary-header {
              background: #f9f9f9; padding: 15px; margin-bottom: 20px;
              border-left: 4px solid #333; display: flex; justify-content: space-between;
            }
            .summary-item { text-align: center; }
            .summary-label { font-size: 8px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
            .summary-value { font-size: 16px; font-weight: 700; margin-top: 5px; }
            .summary-value.highlight { color: #c00; }
            
            /* Table */
            .table-section { margin-bottom: 25px; }
            .table-title { font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; }
            thead th {
              text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 1px;
              color: #666; padding: 10px 8px; border-bottom: 2px solid #333; font-weight: 600;
            }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            tbody td { padding: 10px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
            tbody td.right { text-align: right; font-family: 'Courier New', monospace; }
            tbody td.center { text-align: center; }
            tbody tr:last-child td { border-bottom: 2px solid #333; }
            .status-badge {
              display: inline-block; padding: 3px 8px; border-radius: 3px;
              font-size: 8px; font-weight: 600; text-transform: uppercase;
            }
            .status-pending { background: #fff3cd; color: #856404; }
            .status-overdue { background: #f8d7da; color: #721c24; }
            
            /* Totals */
            .totals-section { display: flex; justify-content: flex-end; margin-bottom: 30px; }
            .totals-box { width: 280px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 10px; }
            .totals-row.subtotal { border-bottom: 1px solid #eee; }
            .totals-row.total { border-top: 2px solid #333; margin-top: 5px; padding-top: 12px; }
            .totals-row.total .label, .totals-row.total .value { font-size: 14px; font-weight: 700; }
            .totals-row .label { color: #666; }
            .totals-row .value { font-family: 'Courier New', monospace; font-weight: 500; }
            
            /* Footer */
            .footer { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; padding-top: 20px; border-top: 1px solid #eee; }
            .footer-section h4 { font-size: 8px; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 15px; }
            .signature-area { height: 60px; border-bottom: 1px solid #333; }
            .signature-label { font-size: 8px; color: #999; text-align: center; margin-top: 5px; }
            .footer-info { font-size: 9px; color: #666; line-height: 1.6; }
            
            /* Notice */
            .notice {
              margin-top: 20px; padding: 15px; background: #f8f9fa;
              border: 1px solid #dee2e6; font-size: 9px; color: #666; text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
              <div class="company-info">
                ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                ${config.direccion ? `${config.direccion}<br>` : ''}
                ${config.telefono ? `Tel: ${config.telefono}` : ''}
                ${config.email ? `<br>${config.email}` : ''}
              </div>
            </div>
            <div class="doc-header">
              <div class="doc-title">Documento</div>
              <div class="doc-type">Estado de Cuenta</div>
              <div class="doc-date">Fecha: ${formattedDate}</div>
            </div>
          </div>
          
          <div class="summary-header">
            <div class="summary-item">
              <div class="summary-label">Total Facturas</div>
              <div class="summary-value">${invoices.length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Facturado</div>
              <div class="summary-value">RD$ ${formatMoney(getTotalFacturas())}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Abonado</div>
              <div class="summary-value">RD$ ${formatMoney(getTotalPagado())}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Saldo Pendiente</div>
              <div class="summary-value highlight">RD$ ${formatMoney(getTotalPendiente())}</div>
            </div>
          </div>
          
          <div class="details-grid">
            <div class="detail-section">
              <h3>Datos del Cliente</h3>
              <div class="detail-row">
                <span class="detail-label">Código</span>
                <span class="detail-value">${selectedClient.codigo_cliente || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Nombre</span>
                <span class="detail-value">${selectedClient.nombre}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">RNC/Cédula</span>
                <span class="detail-value">${selectedClient.rnc || selectedClient.cedula || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Teléfono</span>
                <span class="detail-value">${selectedClient.telefono || '—'}</span>
              </div>
            </div>
            <div class="detail-section">
              <h3>Información de Crédito</h3>
              <div class="detail-row">
                <span class="detail-label">Límite Crédito</span>
                <span class="detail-value">RD$ ${formatMoney(selectedClient.limite_credito || 0)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Crédito Usado</span>
                <span class="detail-value">RD$ ${formatMoney((selectedClient.limite_credito || 0) - (selectedClient.credito_disponible || 0))}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Disponible</span>
                <span class="detail-value">RD$ ${formatMoney(selectedClient.credito_disponible || 0)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Días Crédito</span>
                <span class="detail-value">${selectedClient.dias_credito || 30} días</span>
              </div>
            </div>
          </div>
          
          <div class="table-section">
            <div class="table-title">Detalle de Facturas Pendientes</div>
            <table>
              <thead>
                <tr>
                  <th style="width:15%">Factura</th>
                  <th class="center" style="width:12%">Fecha</th>
                  <th class="center" style="width:12%">Vencimiento</th>
                  <th class="right" style="width:15%">Total</th>
                  <th class="right" style="width:15%">Abonado</th>
                  <th class="right" style="width:15%">Pendiente</th>
                  <th class="center" style="width:16%">Estado</th>
                </tr>
              </thead>
              <tbody>
                ${invoicesData.map(inv => `
                  <tr>
                    <td><strong>${inv.numero}</strong></td>
                    <td class="center">${inv.fecha}</td>
                    <td class="center">${inv.vencimiento}</td>
                    <td class="right">RD$ ${inv.total}</td>
                    <td class="right">RD$ ${inv.pagado}</td>
                    <td class="right"><strong>RD$ ${inv.pendiente}</strong></td>
                    <td class="center">
                      <span class="status-badge ${inv.isOverdue ? 'status-overdue' : 'status-pending'}">
                        ${inv.diasTexto}
                      </span>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="totals-row subtotal">
                <span class="label">Total Facturado</span>
                <span class="value">RD$ ${formatMoney(getTotalFacturas())}</span>
              </div>
              <div class="totals-row">
                <span class="label">Total Abonado</span>
                <span class="value">RD$ ${formatMoney(getTotalPagado())}</span>
              </div>
              <div class="totals-row total">
                <span class="label">Saldo Pendiente</span>
                <span class="value">RD$ ${formatMoney(getTotalPendiente())}</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            <div class="footer-section">
              <h4>Emitido por</h4>
              <div class="footer-info">
                ${config.nombre_comercio || 'LICORSTORE RD'}<br>
                Fecha: ${formattedDate}
              </div>
            </div>
            <div class="footer-section">
              <h4>Recibido por</h4>
              <div class="signature-area"></div>
              <div class="signature-label">Firma del Cliente</div>
            </div>
          </div>
          
          <div class="notice">
            Este documento es un estado de cuenta informativo. Para cualquier consulta o aclaración, 
            comuníquese con nosotros al ${config.telefono || 'teléfono de contacto'}.
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Descargar PDF
  const downloadPDF = async () => {
    if (!selectedClient || invoices.length === 0) {
      toast.error('Seleccione un cliente con facturas pendientes');
      return;
    }

    try {
      toast.info('Generando PDF...');
      
      const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
      const currentDate = new Date();
      const formattedDate = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${currentDate.getFullYear()}`;
      
      // Crear iframe oculto con el contenido A4
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                font-size: 11pt; 
                line-height: 1.5; 
                color: #333; 
                background: #fff;
                padding: 15mm;
                width: 210mm;
              }
              .header { 
                display: flex; 
                justify-content: space-between; 
                align-items: flex-start;
                border-bottom: 3px solid #059669; 
                padding-bottom: 15px; 
                margin-bottom: 20px; 
              }
              .company-info { flex: 1; }
              .company-name { font-size: 22pt; font-weight: bold; color: #059669; margin-bottom: 5px; }
              .company-details { font-size: 9pt; color: #666; }
              .logo { max-width: 120px; max-height: 60px; }
              .document-title { 
                text-align: center; 
                font-size: 18pt; 
                font-weight: bold; 
                color: #333; 
                margin: 20px 0;
                padding: 10px;
                background: #f0fdf4;
                border-radius: 8px;
              }
              .client-section {
                background: #f8fafc;
                padding: 15px;
                border-radius: 8px;
                margin-bottom: 20px;
              }
              .client-section h3 {
                font-size: 12pt;
                color: #059669;
                margin-bottom: 10px;
                border-bottom: 1px solid #e2e8f0;
                padding-bottom: 5px;
              }
              .client-info {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
              }
              .client-info p { font-size: 10pt; margin: 3px 0; }
              .client-info strong { color: #333; }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin: 20px 0; 
                font-size: 10pt;
              }
              th { 
                background: #059669; 
                color: white; 
                padding: 12px 8px; 
                text-align: left; 
                font-weight: 600;
              }
              td { 
                padding: 10px 8px; 
                border-bottom: 1px solid #e2e8f0; 
              }
              tr:nth-child(even) { background: #f8fafc; }
              .text-right { text-align: right; }
              .text-center { text-align: center; }
              .summary {
                margin-top: 20px;
                display: flex;
                justify-content: flex-end;
              }
              .summary-box {
                background: #f8fafc;
                padding: 15px 25px;
                border-radius: 8px;
                border: 2px solid #059669;
                min-width: 300px;
              }
              .summary-row {
                display: flex;
                justify-content: space-between;
                padding: 5px 0;
                font-size: 11pt;
              }
              .summary-row.total {
                border-top: 2px solid #059669;
                margin-top: 10px;
                padding-top: 10px;
                font-size: 14pt;
                font-weight: bold;
                color: #059669;
              }
              .footer {
                margin-top: 30px;
                text-align: center;
                font-size: 9pt;
                color: #666;
                border-top: 1px solid #e2e8f0;
                padding-top: 15px;
              }
              .status-badge {
                display: inline-block;
                padding: 3px 8px;
                border-radius: 12px;
                font-size: 9pt;
                font-weight: 600;
              }
              .status-pending { background: #fef3c7; color: #92400e; }
              .status-overdue { background: #fee2e2; color: #991b1b; }
              .date-info {
                text-align: right;
                font-size: 10pt;
                color: #666;
                margin-bottom: 10px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company-info">
                <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
                <div class="company-details">
                  ${config.rnc ? `<div>RNC: ${config.rnc}</div>` : ''}
                  ${config.telefono ? `<div>Tel: ${config.telefono}</div>` : ''}
                  ${config.direccion ? `<div>${config.direccion}</div>` : ''}
                  ${config.email ? `<div>${config.email}</div>` : ''}
                </div>
              </div>
            </div>

            <div class="document-title">ESTADO DE CUENTA</div>
            
            <div class="date-info">Fecha de emisión: ${formattedDate}</div>

            <div class="client-section">
              <h3>Datos del Cliente</h3>
              <div class="client-info">
                <p><strong>Nombre:</strong> ${selectedClient.nombre}</p>
                <p><strong>Código:</strong> ${selectedClient.codigo_cliente || '-'}</p>
                ${selectedClient.rnc ? `<p><strong>RNC:</strong> ${selectedClient.rnc}</p>` : ''}
                ${selectedClient.telefono ? `<p><strong>Teléfono:</strong> ${selectedClient.telefono}</p>` : ''}
                ${selectedClient.direccion ? `<p><strong>Dirección:</strong> ${selectedClient.direccion}</p>` : ''}
                ${selectedClient.email ? `<p><strong>Email:</strong> ${selectedClient.email}</p>` : ''}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Factura</th>
                  <th class="text-center">Fecha</th>
                  <th class="text-center">Vencimiento</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Pagado</th>
                  <th class="text-right">Pendiente</th>
                  <th class="text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                ${invoices.map(inv => {
                  const daysUntilDue = getDaysUntilDue(inv.fecha_vencimiento);
                  const isOverdue = daysUntilDue < 0;
                  return `
                    <tr>
                      <td><strong>${inv.factura_numero}</strong></td>
                      <td class="text-center">${formatDate(inv.fecha_factura)}</td>
                      <td class="text-center">${formatDate(inv.fecha_vencimiento)}</td>
                      <td class="text-right">RD$ ${(inv.monto_total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2})}</td>
                      <td class="text-right">RD$ ${(inv.monto_pagado || 0).toLocaleString('es-DO', {minimumFractionDigits: 2})}</td>
                      <td class="text-right"><strong>RD$ ${(inv.monto_pendiente || 0).toLocaleString('es-DO', {minimumFractionDigits: 2})}</strong></td>
                      <td class="text-center">
                        <span class="status-badge ${isOverdue ? 'status-overdue' : 'status-pending'}">
                          ${isOverdue ? 'Vencida' : 'Pendiente'}
                        </span>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>

            <div class="summary">
              <div class="summary-box">
                <div class="summary-row">
                  <span>Total Facturado:</span>
                  <span>RD$ ${getTotalFacturas().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
                </div>
                <div class="summary-row">
                  <span>Total Abonado:</span>
                  <span>RD$ ${getTotalPagado().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
                </div>
                <div class="summary-row total">
                  <span>SALDO PENDIENTE:</span>
                  <span>RD$ ${getTotalPendiente().toLocaleString('es-DO', {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </div>

            <div class="footer">
              <p>Total de facturas pendientes: ${invoices.length}</p>
              <p style="margin-top: 10px;">Este documento es un estado de cuenta informativo.</p>
              <p>Generado el ${formattedDate} - ${config.nombre_comercio || 'LICORSTORE RD'}</p>
            </div>
          </body>
        </html>
      `);
      iframeDoc.close();

      // Esperar a que el contenido se renderice
      await new Promise(resolve => setTimeout(resolve, 500));

      // Importar librerías dinámicamente
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      // Capturar el contenido como imagen
      const canvas = await html2canvas(iframeDoc.body, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Crear PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Generar nombre del archivo
      const clientName = selectedClient.nombre.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
      const fileName = `ESTADO_CUENTA_${clientName}_${formattedDate.replace(/\//g, '-')}.pdf`;

      // Descargar
      pdf.save(fileName);

      // Limpiar
      document.body.removeChild(iframe);

      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <FileText className="text-emerald-600" size={36} />
          Estado de Cuenta
        </h1>
        <p className="text-slate-600 mt-2">Consulta las facturas pendientes por cliente</p>
      </div>

      {/* Selector de Cliente */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <Label className="text-base font-semibold mb-2 block">Seleccionar Cliente</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Seleccione un cliente..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex flex-col py-1">
                      <span className="font-semibold">{client.nombre}</span>
                      <span className="text-xs text-slate-500">
                        {client.rnc && `RNC: ${client.rnc}`}
                        {client.telefono && ` | Tel: ${client.telefono}`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedClient && invoices.length > 0 && (
            <div className="flex gap-2">
              {/* Dropdown de Imprimir */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-12">
                    <Printer className="mr-2" size={20} />
                    Imprimir
                    <ChevronDown className="ml-2" size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={printPOS}>
                    <Printer className="mr-2" size={16} />
                    Formato POS (80mm)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={printA4}>
                    <FileText className="mr-2" size={16} />
                    Formato A4
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Botón Descargar PDF */}
              <Button onClick={downloadPDF} className="h-12 bg-red-600 hover:bg-red-700">
                <Download className="mr-2" size={20} />
                Descargar PDF
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Información del Cliente Seleccionado */}
      {selectedClient && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <User className="text-emerald-600" size={24} />
            {selectedClient.nombre}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {selectedClient.codigo_cliente && (
              <div>
                <span className="text-slate-500">Código:</span>
                <span className="ml-2 font-semibold">{selectedClient.codigo_cliente}</span>
              </div>
            )}
            {selectedClient.rnc && (
              <div>
                <span className="text-slate-500">RNC:</span>
                <span className="ml-2 font-semibold">{selectedClient.rnc}</span>
              </div>
            )}
            {selectedClient.telefono && (
              <div>
                <span className="text-slate-500">Teléfono:</span>
                <span className="ml-2 font-semibold">{selectedClient.telefono}</span>
              </div>
            )}
            {selectedClient.direccion && (
              <div className="md:col-span-3">
                <span className="text-slate-500">Dirección:</span>
                <span className="ml-2 font-semibold">{selectedClient.direccion}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resumen de Totales */}
      {selectedClient && invoices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Total Facturado</p>
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(getTotalFacturas())}</p>
              </div>
              <DollarSign className="text-slate-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Total Pagado</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(getTotalPagado())}</p>
              </div>
              <CheckCircle className="text-emerald-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-orange-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Saldo Pendiente</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(getTotalPendiente())}</p>
              </div>
              <AlertCircle className="text-orange-400" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Lista de Facturas */}
      {loadingInvoices ? (
        <div className="bg-white rounded-lg shadow-md p-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : selectedClient && invoices.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 bg-emerald-50 border-b">
            <h3 className="font-bold text-lg">Facturas Pendientes ({invoices.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Factura</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Fecha</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Vencimiento</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Total</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Pagado</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Pendiente</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Estado</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => {
                  const daysUntilDue = getDaysUntilDue(invoice.fecha_vencimiento);
                  const isOverdue = daysUntilDue < 0;
                  
                  return (
                    <tr key={invoice.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-3 font-semibold text-emerald-700">{invoice.factura_numero}</td>
                      <td className="px-4 py-3 text-center text-sm">{formatDate(invoice.fecha_factura)}</td>
                      <td className="px-4 py-3 text-center text-sm">{formatDate(invoice.fecha_vencimiento)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(invoice.monto_total)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(invoice.monto_pagado)}</td>
                      <td className="px-4 py-3 text-right font-bold text-orange-600">{formatCurrency(invoice.monto_pendiente)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          isOverdue 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {isOverdue ? `Vencida (${Math.abs(daysUntilDue)} días)` : `${daysUntilDue} días`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {/* Botón Ver Factura */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewInvoiceDetail(invoice)}
                            className="h-8 w-8 p-0 hover:bg-blue-100"
                            title="Ver Factura"
                          >
                            <Eye size={16} className="text-blue-600" />
                          </Button>
                          
                          {/* Dropdown de Imprimir */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-slate-100"
                                title="Opciones de impresión"
                              >
                                <MoreHorizontal size={16} className="text-slate-600" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewInvoiceDetail(invoice)}>
                                <Eye className="mr-2" size={14} />
                                Ver Factura
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => printInvoicePOS(invoice)}>
                                <Printer className="mr-2" size={14} />
                                Imprimir POS (80mm)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => printInvoiceA4(invoice)}>
                                <FileText className="mr-2" size={14} />
                                Imprimir A4
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : selectedClient ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CheckCircle className="mx-auto text-emerald-500 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-slate-700">Sin facturas pendientes</h3>
          <p className="text-slate-500 mt-2">Este cliente no tiene facturas a crédito pendientes de pago.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <User className="mx-auto text-slate-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-slate-700">Seleccione un cliente</h3>
          <p className="text-slate-500 mt-2">Elija un cliente del listado para ver sus facturas pendientes.</p>
        </div>
      )}

      {/* Modal de Detalle de Factura */}
      <Dialog open={invoiceDetailOpen} onOpenChange={setInvoiceDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {loadingInvoiceDetail ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
          ) : selectedInvoiceDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  Factura #{selectedInvoiceDetail.numero_factura}
                </DialogTitle>
                <DialogDescription>
                  {selectedInvoiceDetail.ncf && `NCF: ${selectedInvoiceDetail.ncf}`}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Información General */}
                <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 p-4 rounded-lg">
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <span className="ml-2 font-semibold">{selectedInvoiceDetail.cliente.nombre}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Fecha:</span>
                    <span className="ml-2 font-semibold">{selectedInvoiceDetail.fecha}</span>
                  </div>
                  {selectedInvoiceDetail.cliente.rnc && (
                    <div>
                      <span className="text-slate-500">RNC:</span>
                      <span className="ml-2 font-semibold">{selectedInvoiceDetail.cliente.rnc}</span>
                    </div>
                  )}
                  {selectedInvoiceDetail.cliente.telefono && (
                    <div>
                      <span className="text-slate-500">Teléfono:</span>
                      <span className="ml-2 font-semibold">{selectedInvoiceDetail.cliente.telefono}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-500">Tipo de Pago:</span>
                    <span className="ml-2 font-semibold capitalize">{selectedInvoiceDetail.tipo_pago}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Estado:</span>
                    <span className={`ml-2 font-semibold ${
                      selectedInvoiceDetail.status === 'activa' || selectedInvoiceDetail.status === 'pagada'
                        ? 'text-green-600'
                        : selectedInvoiceDetail.status === 'anulada'
                        ? 'text-red-600'
                        : 'text-amber-600'
                    }`}>
                      {selectedInvoiceDetail.status === 'activa' ? 'Activa' 
                        : selectedInvoiceDetail.status === 'pagada' ? 'Pagada'
                        : selectedInvoiceDetail.status === 'anulada' ? 'Anulada'
                        : selectedInvoiceDetail.status}
                    </span>
                  </div>
                </div>

                {/* Items de la Factura */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-slate-700">Productos</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2 rounded-tl-lg">Producto</th>
                        <th className="text-right p-2">Cant.</th>
                        <th className="text-right p-2">Precio</th>
                        <th className="text-right p-2 rounded-tr-lg">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoiceDetail.items.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2">{item.producto_nombre}</td>
                          <td className="text-right p-2">{item.cantidad}</td>
                          <td className="text-right p-2">{formatCurrency(item.precio_unitario)}</td>
                          <td className="text-right p-2 font-semibold">{formatCurrency(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totales */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal:</span>
                    <span>{formatCurrency(selectedInvoiceDetail.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">ITBIS (18%):</span>
                    <span>{formatCurrency(selectedInvoiceDetail.itbis)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Total:</span>
                    <span className="text-emerald-600">{formatCurrency(selectedInvoiceDetail.total)}</span>
                  </div>
                </div>

                {/* Indicador de Crédito */}
                {selectedInvoiceDetail.es_credito && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-center">
                    <span className="font-bold text-amber-700">FACTURA A CRÉDITO</span>
                    {selectedInvoiceDetail.dias_credito && (
                      <span className="ml-2 text-amber-600">({selectedInvoiceDetail.dias_credito} días)</span>
                    )}
                  </div>
                )}

                {/* Botones de Acción */}
                <div className="flex gap-3 pt-4 border-t">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 bg-blue-600 hover:bg-blue-700">
                        <Printer className="mr-2" size={16} />
                        Imprimir
                        <ChevronDown className="ml-2" size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => {
                        const accRec = invoices.find(inv => inv.factura_id === selectedInvoiceDetail.id);
                        if (accRec) printInvoicePOS(accRec);
                      }}>
                        <Printer className="mr-2" size={14} />
                        Formato POS (80mm)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {
                        const accRec = invoices.find(inv => inv.factura_id === selectedInvoiceDetail.id);
                        if (accRec) printInvoiceA4(accRec);
                      }}>
                        <FileText className="mr-2" size={14} />
                        Formato A4
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setInvoiceDetailOpen(false)}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-slate-500">
              No se pudo cargar la información de la factura
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstadoCuenta;
